import { useState } from "react";
import { addDays, format, startOfDay } from "date-fns";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { useCreateNoteBo } from "@/api/notes.api";
import { NoteType } from "@/constants/enums/noteType.enum";
import { NoteSubType } from "@/constants/enums/noteSubType.enum";
import { cn } from "@/lib/utils";
import {
  LATE_SHIPPING_QUALIFICATION_OPTIONS,
  NOTE_DATE_FORMAT,
  getLateShippingQualificationNote,
  getLateShippingQualificationOption,
  parseQualificationDate,
  type LateShippingQualificationOption,
} from "@/components/backOffice/shared/lateShippingQualificationBadge";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

const defaultDateFor = (option: LateShippingQualificationOption): Date | undefined =>
  option.defaultDaysFromNow === null
    ? undefined
    : addDays(startOfDay(new Date()), option.defaultDaysFromNow);

export const LateShippingQualificationModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation(["boLogistics", "common"]);
  const translateMessage = useTranslateMessage();
  const existingNote = getLateShippingQualificationNote(expedition.expeditionNotes);
  const existingOption = getLateShippingQualificationOption(existingNote?.subType);
  const existingDate = existingOption ? parseQualificationDate(existingNote?.note) : undefined;

  const [selected, setSelected] = useState<NoteSubType | undefined>(existingOption?.subType);
  const [date, setDate] = useState<Date | undefined>(existingDate);
  const [month, setMonth] = useState<Date>(existingDate ?? new Date());

  const { mutateAsync: createNote, isPending } = useCreateNoteBo(
    t("lateShippingModal.createSuccess"),
  );

  const selectedOption = getLateShippingQualificationOption(selected);

  const canSubmit = !!selectedOption && (!selectedOption.dateLabel || !!date);

  const handleSelectOption = (option: LateShippingQualificationOption) => {
    if (option.subType === selected) return;
    const nextDate = defaultDateFor(option);
    setSelected(option.subType);
    setDate(nextDate);
    setMonth(nextDate ?? new Date());
  };

  const handleSubmit = async () => {
    if (!selectedOption || !canSubmit) return;

    await createNote({
      id: expedition.id,
      payload:
        selectedOption.dateLabel && date
          ? format(date, NOTE_DATE_FORMAT)
          : translateMessage(selectedOption.title) ?? selectedOption.title,
      type: NoteType.LATE_SHIPPING_QUALIFICATION,
      subType: selectedOption.subType,
    });

    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("lateShippingModal.title")}
      size="sm"
      dismissible
      footer={
        <div className="flex w-full flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending || !canSubmit}>
            {isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              t("lateShippingModal.submit")
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        {LATE_SHIPPING_QUALIFICATION_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = option.subType === selected;

          return (
            <button
              key={option.subType}
              type="button"
              onClick={() => handleSelectOption(option)}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg p-3 text-left transition-colors",
                isSelected ? "bg-gray-100" : "hover:bg-gray-50",
              )}
            >
              <Icon className={cn("mt-0.5 size-5 shrink-0", option.iconClassName)} />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-gray-900">
                  {translateMessage(option.title)}
                </span>
                <span className="text-xs text-gray-500">{translateMessage(option.helper)}</span>
              </div>
            </button>
          );
        })}

        {selectedOption?.dateLabel && (
          <div className="flex flex-col items-center gap-1 border-t border-gray-200 pt-4">
            <div className="flex w-full items-baseline gap-2">
              <Label className="shrink-0">
                {translateMessage(selectedOption.dateLabel ?? undefined)}
              </Label>
              <span className="text-sm font-semibold text-gray-900">
                {date ? format(date, "dd/MM/yyyy") : "—"}
              </span>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              month={month}
              onMonthChange={setMonth}
              required={false}
              fixedWeeks
              showOutsideDays={false}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
